import { ListItem, Spinner, theme } from 'aimo-ui';
import WalletAddedBadge from 'components/payment/WalletAddedBadge';
import useAddPaymentMethod from 'hooks/useAddPaymentMethod';
import useCanAddMobilePay from 'hooks/useCanAddMobilePay';
import useWalletAddRows from 'hooks/useWalletAddRows';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useWalletTokenVerifyStore } from 'stores/walletTokenVerifyStore';
import { StyledWrapper } from 'styles/ContainerStyles';
import { ListContainer } from 'styles/ListStyle';
import type {
  AddPaymentMethodProps,
  PaymentDataProps,
} from 'types/PaymentData';
import { resolvePaymentMethodsAddedLabel } from 'utils/paymentUtils';
import { testIdentifiers } from 'utils/testIdentifiers';
import Icon from '../icons/Icon';
import { useUiStore } from 'stores/uiStore';

const ArrowIcon = () => <Icon name="Chevron_Right" size="s" />;

type AddRow = Pick<
  PaymentDataProps,
  'title' | 'icon' | 'testID' | 'iconRight'
> & {
  onPress: () => void;
  isLoading: boolean;
  alreadyAdded?: boolean;
};

const WalletAddedRightContent = ({ item }: { item: AddRow }) => {
  const { t } = useTranslation();
  return (
    <WalletAddedBadge
      label={resolvePaymentMethodsAddedLabel(t)}
      testID={`${item.testID}-added`}
    />
  );
};

const AddPaymentMethodItem = ({
  row,
  indexValue,
}: {
  row: AddRow;
  indexValue: number;
}) => {
  const { t } = useTranslation();
  const {
    title,
    icon,
    iconRight,
    testID,
    onPress,
    isLoading,
    alreadyAdded = false,
  } = row;
  const label = t(title);

  if (isLoading) {
    return (
      <StyledWrapper center testID="add-payment-card-form-loading">
        <Spinner />
      </StyledWrapper>
    );
  }

  return (
    <ListItem
      item={row}
      accessibilityRole="button"
      accessible={true}
      accessibilityLabel={
        alreadyAdded
          ? `${label}. ${resolvePaymentMethodsAddedLabel(t)}`
          : undefined
      }
      indexValue={indexValue}
      title={label}
      onPress={alreadyAdded ? undefined : onPress}
      testID={testID}
      withBackground={theme.colors.gray50}
      iconName={icon}
      iconComponent={Icon}
      arrowComponent={iconRight && !alreadyAdded ? ArrowIcon : null}
      rightContentComponent={alreadyAdded ? WalletAddedRightContent : undefined}
    />
  );
};

const AddPaymentMethod = ({
  paymentData,
  isFlex = false,
  isOnboarding = false,
}: AddPaymentMethodProps) => {
  const {
    addCard,
    addWallet,
    loadingAddCard,
    loadingAddMobilePay,
    pendingWalletType,
  } = useAddPaymentMethod();
  const setMobilePayInitiatedFromOnboarding = useWalletTokenVerifyStore(
    (state) => state.setMobilePayInitiatedFromOnboarding
  );
  const canAddMobilePay = useCanAddMobilePay();
  const walletRows = useWalletAddRows();
  const hasOtherMethods = canAddMobilePay || walletRows.length > 0;

  const handleAddPress = useCallback(
    (item: PaymentDataProps) => {
      // More than one method on offer (MobilePay and/or a stored wallet in
      // addition to card) → route through the selection modal instead of
      // jumping straight into the card flow.
      if (!isOnboarding && hasOtherMethods) {
        useUiStore.getState().setSheetModal('addOtherPaymentMethod');
        return;
      }
      if (item.action === 'addMobilePay') {
        if (isOnboarding) {
          setMobilePayInitiatedFromOnboarding(true);
        }
        useUiStore.getState().setSheetModal('mobilePayConfirmation');
        return;
      }
      addCard(item);
    },
    [
      addCard,
      hasOtherMethods,
      isOnboarding,
      setMobilePayInitiatedFromOnboarding,
    ]
  );

  const validPaymentItems =
    paymentData?.filter((item) => {
      if (!item) return false;
      if (item.action === 'addMobilePay') {
        return canAddMobilePay;
      }
      return true;
    }) ?? [];

  if (validPaymentItems.length === 0) {
    return null;
  }

  // Onboarding lists the addable wallets right after its card row. Outside
  // onboarding the card row routes to the selection sheet, which lists them.
  const walletAddRows: AddRow[] = walletRows.map(
    ({ walletType, alreadyAdded, labelKey, icon, testID }) => ({
      title: labelKey,
      icon,
      testID,
      iconRight: 'Chevron_Right',
      alreadyAdded,
      isLoading: pendingWalletType === walletType,
      onPress: () => addWallet(walletType, 'onboarding'),
    })
  );

  const rows = validPaymentItems.flatMap((item): AddRow[] => [
    {
      ...item,
      isLoading:
        item.action === 'addMobilePay' ? loadingAddMobilePay : loadingAddCard,
      onPress: () => handleAddPress(item),
    },
    ...(item.action === 'addCardOnboarding' ? walletAddRows : []),
  ]);

  return (
    <ListContainer
      isFlex={isFlex}
      {...testIdentifiers('add-credit-card-button-container')}>
      {rows.map((row, index) => (
        <AddPaymentMethodItem
          key={row.testID ?? `payment-method-${index}`}
          row={row}
          indexValue={index}
        />
      ))}
    </ListContainer>
  );
};

export default AddPaymentMethod;
